/**
 * Pardot integration utilities
 */

export interface PardotFormData {
  email: string;
  firstName?: string;
  lastName?: string;
  userId?: string;
  organisationId?: string;
}

/**
 * Sends user data to Pardot form handler using fetch (POST)
 * @param formHandlerUrl The Pardot form handler URL (from environment variables)
 * @param data The form data to send
 */
export const sendToPardotFormHandler = async (
  formHandlerUrl: string | undefined,
  data: PardotFormData,
): Promise<void> => {
  if (!formHandlerUrl) {
    console.warn("Pardot form handler URL not configured");
    return;
  }

  try {
    const formData = new URLSearchParams();
    formData.append("email", data.email);

    if (data.firstName) {
      formData.append("firstName", data.firstName);
    }

    if (data.lastName) {
      formData.append("lastName", data.lastName);
    }

    if (data.userId) {
      formData.append("dodUserId", data.userId);
    }

    if (data.organisationId) {
      formData.append("dodOrganisationId", data.organisationId);
    }

    await fetch(formHandlerUrl, {
      method: "POST",
      body: formData,
      mode: "no-cors", // Pardot form handlers typically don't support CORS
    });
  } catch (error) {
    console.error("Failed to send data to Pardot:", error);
    // Don't throw - we don't want to fail the primary operation if Pardot tracking fails
  }
};

/**
 * Builds a URL with query parameters for Pardot form handler (for iframe approach)
 * @param formHandlerUrl The Pardot form handler URL (from environment variables)
 * @param data The form data to send
 * @returns The complete URL with query parameters for use in iframe src
 */
export const buildPardotIframeUrl = (
  formHandlerUrl: string | undefined,
  data: PardotFormData,
): string | null => {
  if (!formHandlerUrl) {
    console.warn("Pardot form handler URL not configured");
    return null;
  }

  try {
    const params = new URLSearchParams();
    params.append("email", data.email);

    if (data.firstName) {
      params.append("firstName", data.firstName);
    }

    if (data.lastName) {
      params.append("lastName", data.lastName);
    }

    if (data.userId) {
      params.append("dodUserId", data.userId);
    }

    if (data.organisationId) {
      params.append("dodOrganisationId", data.organisationId);
    }

    return `${formHandlerUrl}?${params.toString()}`;
  } catch (error) {
    console.error("Failed to build Pardot iframe URL:", error);
    return null;
  }
};
